<script lang="ts">
    import { getLibxert } from '@/xert-wasm';
    import { getFocusDuration, SpecificityTypes, specificityValues } from '../../Common/constants';
    import Input from '@/lib/components/ui/input/input.svelte';
    import { Label } from '@/lib/components/ui/label';
    import Textarea from '@/lib/components/ui/textarea/textarea.svelte';
    import * as Select from '@/lib/components/ui/select';
    import Separator from '@/lib/components/ui/separator/separator.svelte';
    import * as RadioGroup from '@/lib/components/ui/radio-group';
    import { cn } from '@/lib/utils';
    import { round, xssToFocusSpecificity, kmToUserDistanceUnits, userDistanceUnit } from '@/Util/utils';
    import { onMount } from 'svelte';

    export let myFitness;
    export let exercise;
    export let activity_types
    export let isValid = false;

    //@ts-ignore
    const jq = window.$;

    let exerciseIsNull      = (exercise == null);
    export let exercisePath        = (!exerciseIsNull) ? exercise.path : null;
    export let exerciseName        = (!exerciseIsNull) ? exercise.name : null;
    export let exerciseDuration    = (!exerciseIsNull) ? Math.round(exercise.duration/60) : 0;
    export let exerciseXss         = (!exerciseIsNull) ? exercise.xss : 0;
    export let exerciseXlss         = (!exerciseIsNull) ? exercise.xlss : 0;
    export let exerciseXhss         = (!exerciseIsNull) ? exercise.xhss : 0;
    export let exerciseXpss         = (!exerciseIsNull) ? exercise.xpss : 0;
    export let exerciseDistance    = (!exerciseIsNull) ? kmToUserDistanceUnits(exercise.distance): 0;
    export let exerciseFocus       = (!exerciseIsNull) ? exercise.focus : "Endurance";
    export let exerciseFocusDuration = (!exerciseIsNull) ? exercise.sfd : null;
    export let activityType        = (!exerciseIsNull) ? exercise.type ?? "Cycling" : "Cycling";
    export let exerciseSpr         = (!exerciseIsNull) ? exercise.spr : "Mixed";
    export let exerciseSp          = (!exerciseIsNull) ? exercise.sp : null;
    export let exerciseDesc        = (!exerciseIsNull) ? exercise.description : null;

    const { userParams, trainingAdvice } = myFitness

    let exerciseFocusSelection = exerciseFocus ? {
        label: exerciseFocus,
        value: exerciseFocus,
    } : undefined
    
    let exerciseSprSelection = exerciseSpr ? {
        label: exerciseSpr,
        value: exerciseSpr,
    } : undefined
    let activityTypeSelection = activityType ? {
        label: activityType,
        value: activityType,
    } : undefined

    let exerciseNamePlaceholder = ""
    let exerciseNameField = exerciseName;
    $: exerciseNamePlaceholder = `${exerciseSpr} ${exerciseFocus} Training`
    function updateExerciseName(name, field, placeholder) {
        return field || placeholder
    }
    $: exerciseName = updateExerciseName(exerciseName, exerciseNameField, exerciseNamePlaceholder)

    let xssInputType = 'lhp'
    let focus_types = Object.keys($userParams.athletes).map(key => $userParams.athletes[key].workoutname);

    // The following 4 values are used to update the XSS components reactively to changes in total XSS
    let xssComponentTotal;
    let xlssRatio;
    let xhssRatio;
    let xpssRatio;
    $: exercisePath, updateXssComponentRatios();

    let areXssValuesPossible = true
    let showXssWarning = false
    let xssErrorMessage = ""

    let exerciseDate        = (!exerciseIsNull) ? exercise.start_date : null;
    
    let distanceEditable    = (exerciseDate == null) || (exerciseDate != null && (exerciseDate > new Date().toISOString()));

    export function initForm(handler) {
        jq('#edit-form' + exercisePath).validate({ 
            rules: {
                ['title' + exercisePath]: {
                    minlength: 2,
                    required: true
                },
                ['duration' + exercisePath]: {
                    required: true,
                    digits: true,
                    min: 1
                },
                ['xss' + exercisePath]: {
                    required: true,
                    number: true,
                    min: 1
                },
                ['distance' + exercisePath]: {
                    number: true,
                    min: 0
                }
            },
            messages: {
                ['title' + exercisePath]: 'Please enter a title',
                ['duration' + exercisePath]: 'Please enter a duration in minutes',
                ['xss' + exercisePath]: 'Please enter a valid strain score (XSS)',
                ['distance' + exercisePath]: 'Please enter a valid distance'
            },
            submitHandler: handler
        });
        
        updateValidity()
    }

    function updateValidity() {
        isValid = jq('#edit-form' + exercisePath).valid() && areXssValuesPossible;
    }
    
    onMount(() => {
        updateValidity();
	});

    async function updateXssComponentRatios() {
        xssComponentTotal = exerciseXlss + exerciseXhss + exerciseXpss
        if (xssComponentTotal == 0) {
            const sig = $trainingAdvice.signature
            const libxert = await getLibxert()
            const sp = specificityValues[SpecificityTypes[exerciseSpr]]
            const focusDuration = getFocusDuration($userParams.athletes, exerciseFocus)
            const lhpxssRatios = libxert.power_alloc(sig.ftp, sig.atc, sig.pp, 1, focusDuration, sp);
            xlssRatio = lhpxssRatios[0]
            xhssRatio = lhpxssRatios[1]
            xpssRatio = 1 - xlssRatio - xhssRatio
        } else {
            xlssRatio = exerciseXlss / xssComponentTotal
            xhssRatio = exerciseXhss / xssComponentTotal
            xpssRatio = exerciseXpss / xssComponentTotal
        }
    }

    // scale XSS components (low, high, peak) proportionally to XSS total change
    function onXssTotalChange() {
        if (exerciseXlss != null) {
            exerciseXlss = round(exerciseXss * xlssRatio, 1)
            exerciseXhss = round(exerciseXss * xhssRatio, 1)
            exerciseXpss = round(exerciseXss * xpssRatio, 1)
        }
        
        updateValidity()
    }

    // update XSS components (low, high, peak) according to focus and specificity
    async function onFocusSpecificityChange(f: string, s: string) {
        exerciseFocus = f ?? "Endurance";
        exerciseSpr = s ?? "Mixed";
        
        const sig = $trainingAdvice.signature
        const libxert = await getLibxert()
        const sp = specificityValues[SpecificityTypes[exerciseSpr]]
        const focusDuration = getFocusDuration($userParams.athletes, exerciseFocus)
        const lhpxssRatios = libxert.power_alloc(sig.ftp, sig.atc, sig.pp, 1, focusDuration, sp);
        exerciseSp = sp
        exerciseFocusDuration = focusDuration

        xlssRatio = lhpxssRatios[0]
        xhssRatio = lhpxssRatios[1]
        xpssRatio = 1 - xlssRatio - xhssRatio
        exerciseXlss = round(lhpxssRatios[0] * exerciseXss, 1)
        exerciseXhss = round(lhpxssRatios[1] * exerciseXss, 1)
        exerciseXpss = round(exerciseXss - exerciseXlss - exerciseXhss, 1)
        areXssValuesPossible = true
        showXssWarning = false
        
        updateValidity()
    }

    // update total XSS, focus, and specificity to match XSS components (low, high, peak)
    async function onXssComponentChange() {
        const lxss = +(exerciseXlss ?? 0)
        const hxss = +(exerciseXhss ?? 0)
        const pxss = +(exerciseXpss ?? 0)
        exerciseXss = round(lxss + hxss + pxss, 1)
        const sig = $trainingAdvice.signature
        const focusSpecificity = await xssToFocusSpecificity(sig, {xss: exerciseXss, xlss: lxss, xhss: hxss, xpss: pxss}, $userParams.athletes)
        const sp = focusSpecificity.specificity
        const f = focusSpecificity.sfd
        areXssValuesPossible = !(f == 0 || sp <= 0 || sp > 1 || ((hxss > 0) != (pxss > 0)));
        showXssWarning = areXssValuesPossible && (sp < 0.05)
        xssErrorMessage = 'XSS values cannot be performed.'

        exerciseSp = sp
        exerciseFocusDuration = f

        exerciseSpr = focusSpecificity.specificityRating
        exerciseSprSelection = {
            value: exerciseSpr,
            label: exerciseSpr,
        }
        exerciseFocus = focusSpecificity.focus
        exerciseFocusSelection = {
            value: exerciseFocus,
            label: exerciseFocus,
        }
        
        await updateXssComponentRatios()
        updateValidity()
    }

</script>

<form id="edit-form{exercisePath}" class='flex flex-col gap-8'>
    <div class="">
        <Label class="font-semibold text-sm">Title</Label>
        <Input type="text" class="form-control input-circle-right" id="title{exercisePath}" name="title{exercisePath}" placeholder={exerciseNamePlaceholder} bind:value={exerciseNameField} minlength={2} on:input={updateValidity}/>
    </div>
    
    <div class="">
        <Label class="font-semibold text-sm">Description</Label>
        <Textarea id="description{exercisePath}" class="form-control input-circle-right" rows={3} bind:value={exerciseDesc} name="description{exercisePath}" placeholder="Write any comments about the activity"/>
    </div>

    <div class="flex flex-row flex-wrap gap-3">
        <div class="min-w-[180px] flex-1">
            <Label class="font-semibold text-sm">Activity Type</Label>
            <Select.Root bind:selected={activityTypeSelection} items={activity_types} onSelectedChange={(v) => activityType = v.value}>
                <Select.Trigger>
                    <Select.Value placeholder="Activity Type"/>
                </Select.Trigger>
                <Select.Content class="overflow-auto max-h-48">
                    {#if activity_types != null}
                        {#each Object.entries(activity_types).sort() as [key]}
                            <Select.Item value="{key}" label={key}>{key}</Select.Item>
                        {/each}
                    {/if}
                </Select.Content>
            </Select.Root>
        </div>
        <div class="min-w-[180px] flex-1">
            <Label class="font-semibold text-sm">Duration <span class="text-muted-foreground">(min)</span></Label>
            <Input id="duration{exercisePath}" name="duration{exercisePath}" type="number" min=0 step=1 class="form-control input-circle-right" required bind:value={exerciseDuration} on:input={updateValidity}/>
        </div>
        <div class="min-w-[180px] flex-1">
            <Label class="font-semibold text-sm">Distance <span class="text-muted-foreground">({userDistanceUnit()})</span></Label>
            <Input id="distance{exercisePath}" name="distance{exercisePath}" type="number" min=0 step=1 class="form-control input-circle-right" bind:value={exerciseDistance} placeholder="Distance..." readonly="{!distanceEditable}" on:input={updateValidity}/>
        </div>
    </div>

    <Separator/>

    <div class="form-group">
        <Label class="font-semibold text-sm">XSS</Label>
        <Input id="xss{exercisePath}" name="xss{exercisePath}" type="number" min=0 step=1 class="form-control input-circle-right max-w-[280px]" bind:value={exerciseXss} on:input={onXssTotalChange}/>
    </div>

    <div>
        <Label class="font-semibold text-sm">Enter the XSS values with...</Label>
        <RadioGroup.Root bind:value={xssInputType} class="flex flex-row gap-3 flex-wrap">
            <Label class="flex flex-row items-center gap-2 relative border rounded-b-md rounded-t-md px-4 py-2 cursor-pointer hover:border-primary [&:has(button[data-state='checked'])]:border-primary" for="lhp">   
                <RadioGroup.Item class="" value="lhp" id="lhp"/>    
                <div class="font-semibold text-sm">Low/High/Peak</div>
            </Label>
            <Label class="flex flex-row items-center gap-2 relative border rounded-b-md rounded-t-md px-4 py-2 cursor-pointer hover:border-primary [&:has(button[data-state='checked'])]:border-primary" for="focus">   
                <RadioGroup.Item class="" value="focus" id="focus"/>    
                <div class="font-semibold text-sm">Focus/Specificity</div>
            </Label>
        </RadioGroup.Root>
    </div>

    {#if xssInputType == 'lhp'}
        <div>
            <div class="flex flex-row flex-wrap gap-3">
                <div class="min-w-[180px] flex-1">
                    <Label class="font-medium text-sm text-xertlow">Low XSS</Label>
                    <Input id="xlss{exercisePath}" name="xlss{exercisePath}" type="number" min=0 step=1 class="form-control input-circle-right" bind:value={exerciseXlss} placeholder="Low XSS..." on:input={onXssComponentChange}/>
                </div>
                <div class="min-w-[180px] flex-1">
                    <Label class="font-medium text-sm text-xerthigh">High XSS</Label>
                    <Input id="xhss{exercisePath}" name="xhss{exercisePath}" type="number" min=0 step=1 class="form-control input-circle-right" bind:value={exerciseXhss} placeholder="High XSS..." on:input={onXssComponentChange}/>
                </div>
                <div class="min-w-[180px] flex-1">
                    <Label class="font-medium text-sm text-xertpeak">Peak XSS</Label>
                    <Input id="xpss{exercisePath}" name="xpss{exercisePath}" type="number" min=0 step=1 class="form-control input-circle-right" bind:value={exerciseXpss} placeholder="Peak XSS..." on:input={onXssComponentChange}/>
                </div>
            </div>
            {#if !areXssValuesPossible || showXssWarning}
                <p class={cn(showXssWarning ? "text-yellow-600" : "text-red-600")}>{xssErrorMessage}</p>
            {/if}
        </div>
        <div class="flex flex-row gap-3 flex-wrap">
            <div><span class="font-semibold text-sm">Focus: </span>  {exerciseFocus}</div>
            <div><span class="font-semibold text-sm">Specificity:</span>  {exerciseSpr}</div>
        </div>
    {:else}
        <div>
            <div class="flex flex-row flex-wrap gap-3">
                <div class="min-w-[180px] flex-1">
                    <Label class="font-semibold text-sm">Focus</Label>
                    <Select.Root bind:selected={exerciseFocusSelection} items={focus_types} onSelectedChange={(v) => onFocusSpecificityChange(v.value, exerciseSpr)}>
                        <Select.Trigger>
                            <Select.Value/>
                        </Select.Trigger>
                        <Select.Content class="overflow-auto max-h-48">
                            {#if focus_types != null}
                                {#each focus_types as value}
                                    <Select.Item value="{value}" label={value}>{value}</Select.Item>
                                {/each}
                            {/if}
                        </Select.Content>
                    </Select.Root>
                </div>
                <div class="min-w-[180px] flex-1">
                    <Label class="font-semibold text-sm">Specificity</Label>
                    <Select.Root bind:selected={exerciseSprSelection} onSelectedChange={(v) => onFocusSpecificityChange(exerciseFocus, v.value)}>
                        <Select.Trigger>
                            <Select.Value/>
                        </Select.Trigger>
                        <Select.Content class="overflow-auto max-h-48">
                            <Select.Item value="Pure" label="Pure">Pure</Select.Item>
                            <Select.Item value="Mixed" label="Mixed">Mixed</Select.Item>
                            <Select.Item value="Polar" label="Polar">Polar</Select.Item>
                        </Select.Content>
                    </Select.Root>
                </div>
            </div>
            {#if !areXssValuesPossible || showXssWarning}
                <p class={cn(showXssWarning ? "text-yellow-600" : "text-red-600")}>{xssErrorMessage}</p>
            {/if}
        </div>
        <div class="flex flex-row gap-3 flex-wrap">
            <div><span class="font-medium text-sm text-xertlow">Low XSS:</span>  {exerciseXlss}</div>
            <div><span class="font-medium text-sm text-xerthigh">High XSS:</span>  {exerciseXhss}</div>
            <div><span class="font-medium text-sm text-xertpeak">Peak XSS:</span>  {exerciseXpss}</div>
        </div>
    {/if}
</form>